import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sparkles, FileText, Settings, Send, Download, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-gray-200 text-gray-700',
  issued: 'bg-blue-100 text-blue-700',
  sent: 'bg-orange-100 text-orange-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const authHeaders = () => {
  const token = localStorage.getItem('sunify_token');
  return { Authorization: `Bearer ${token}` };
};

const fmtMoney = (n, c = 'EUR') => {
  const sym = c === 'EUR' ? '€' : c === 'USD' ? '$' : c === 'CZK' ? 'Kč' : c;
  return `${Number(n || 0).toFixed(2)} ${sym}`;
};

const useUiLang = () => {
  const { i18n } = useTranslation();
  return (i18n.language || 'sk').split('-')[0];
};

const fmtDate = (s, lang = 'sk') => {
  if (!s) return '';
  const d = new Date(s);
  const map = { sk: 'sk-SK', cs: 'cs-CZ', en: 'en-US', de: 'de-DE', ru: 'ru-RU' };
  return d.toLocaleDateString(map[lang] || 'sk-SK');
};

// ---------- Settings tab ----------
const SettingsTab = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/company-settings`, { headers: authHeaders() })
      .then((r) => setSettings(r.data))
      .catch(() => setSettings(null));
  }, []);

  if (!settings) {
    return <div className="p-6">{t('admin.invoices_page.loading', 'Loading…')}</div>;
  }

  const update = (k, v) => setSettings({ ...settings, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/company-settings`, settings, { headers: authHeaders() });
      setSettings(data);
      setSavedAt(new Date().toLocaleTimeString());
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ k, label, type = 'text' }) => (
    <div>
      <Label htmlFor={`field-${k}`}>{label}</Label>
      <Input
        id={`field-${k}`}
        type={type}
        value={settings[k] ?? ''}
        onChange={(e) => update(k, type === 'number' ? Number(e.target.value) : e.target.value)}
        data-testid={`settings-${k}`}
      />
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <h3 className="text-lg font-semibold">{t('admin.invoices_page.seller_details', 'Reqvizity / Údaje predajcu')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field k="name" label={t('admin.invoices_page.field_name', 'Názov spoločnosti')} />
        <Field k="email" label={t('admin.invoices_page.field_email', 'E-mail')} />
        <Field k="phone" label={t('admin.invoices_page.field_phone', 'Telefón')} />
        <Field k="address_street" label={t('admin.invoices_page.field_street', 'Ulica')} />
        <Field k="address_city" label={t('admin.invoices_page.field_city', 'Mesto')} />
        <Field k="address_postal_code" label={t('admin.invoices_page.field_zip', 'PSČ')} />
        <Field k="address_country" label={t('admin.invoices_page.field_country', 'Krajina (ISO-2: SK, CZ, DE...)')} />
        <Field k="ico" label={t('admin.invoices_page.field_ico', 'IČO')} />
        <Field k="dic" label={t('admin.invoices_page.field_dic', 'DIČ / VAT ID')} />
        <Field k="ic_dph" label={t('admin.invoices_page.field_ic_dph', 'IČ DPH (voliteľné)')} />
        <Field k="bank_name" label={t('admin.invoices_page.field_bank', 'Banka')} />
        <Field k="iban" label="IBAN" />
        <Field k="swift_bic" label="SWIFT / BIC" />
        <Field k="default_vat_rate" label={t('admin.invoices_page.field_vat_rate', 'Štandardná sadzba DPH (%)')} type="number" />
        <Field k="invoice_due_days" label={t('admin.invoices_page.field_due_days', 'Splatnosť (dni)')} type="number" />
        <Field k="invoice_number_prefix" label={t('admin.invoices_page.field_prefix', 'Prefix čísla faktúry (napr. FA-)')} />
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save', 'Uložiť')}
        </Button>
        {savedAt && <span className="text-sm text-green-600">{t('admin.invoices_page.saved_at', 'Uložené')} {savedAt}</span>}
      </div>
    </div>
  );
};

// ---------- AI Generator tab ----------
const AiTab = ({ onCreated }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/invoices/ai-generate`,
        { text },
        { headers: authHeaders() }
      );
      setResult(data.invoice);
      setText('');
      if (onCreated) onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const examples = [
    'Vytvor faktúru pre Petra Horvátha, peter@gmail.com, 3 ks fólie Sunify Premium za 89 EUR za kus, doprava DPD 4.50 EUR do Bratislavy, splatnosť 10 dní',
    'Invoice AlphaTec GmbH, billing@alpha.de, VAT DE123456789, 50 rolls of solar film at 75 EUR each, DHL delivery to Munich for 15 EUR',
    'Faktura pro Karolínu Novákovou, novakova@email.cz, 2 ks instalačního setu po 45 EUR, GLS Praha, splatnost 14 dní',
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-orange-500" />
          {t('admin.invoices_page.ai_title', 'AI Generátor faktúr')}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('admin.invoices_page.ai_desc',
            'Popíšte faktúru voľným textom v slovenčine, češtine, angličtine, nemčine alebo ruštine. AI agent (Claude Sonnet 4.5) ju vytvorí ako koncept, ktorý si môžete skontrolovať a poslať klientovi.')}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={t('admin.invoices_page.ai_placeholder', 'Napr.: Vytvor faktúru pre Jana Nováka, jan@email.sk, 5 ks fólie za 120 EUR/ks, doprava DPD...')}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono text-sm"
          data-testid="ai-invoice-input"
        />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={loading || !text.trim()} data-testid="ai-generate-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {t('admin.invoices_page.ai_generate', 'Vygenerovať návrh')}
          </Button>
          {examples.map((ex, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setText(ex)}
              className="text-xs px-3 py-1 border border-gray-300 rounded-full hover:bg-gray-50"
            >
              {t('admin.invoices_page.example', 'Príklad')} {i + 1}
            </button>
          ))}
        </div>
      </form>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="ai-error">
          {error}
        </div>
      )}

      {result && (
        <div className="p-4 border-2 border-green-200 bg-green-50 rounded-lg" data-testid="ai-result">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-semibold">{t('admin.invoices_page.draft_created', 'Vytvorený návrh')}: {result.number}</div>
              <div className="text-sm text-gray-600">
                {result.buyer_name} — {fmtMoney(result.total, result.currency)}
              </div>
            </div>
            <Badge className={STATUS_COLORS[result.status] || ''}>{result.status}</Badge>
          </div>
          <div className="text-xs text-gray-600">
            {result.items?.length || 0} {t('admin.invoices_page.items_word', 'položiek')} • {t('admin.invoices_page.vat', 'DPH')} {result.is_reverse_charge ? '0% (reverse charge)' : `${result.vat_total.toFixed(2)} ${result.currency}`} •
            {' '}{t('admin.invoices_page.due', 'Splatnosť')} {fmtDate(result.due_date)}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Invoice list ----------
const InvoiceList = ({ refreshKey, onChanged }) => {
  const { t } = useTranslation();
  const lang = useUiLang();
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const url = `${API}/admin/invoices${filter ? `?status=${filter}` : ''}`;
    const { data } = await axios.get(url, { headers: authHeaders() });
    setInvoices(data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, refreshKey]);

  const downloadPdf = async (id, number) => {
    setBusyId(id);
    try {
      const r = await axios.get(`${API}/admin/invoices/${id}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Faktura_${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusyId(null);
    }
  };

  const sendEmail = async (id) => {
    if (!window.confirm(t('admin.invoices_page.send_confirm', 'Poslať faktúru e-mailom klientovi?'))) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/invoices/${id}/send`, {}, { headers: authHeaders() });
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (id) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/invoices/${id}`, { status: 'paid' }, { headers: authHeaders() });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('admin.invoices_page.delete_confirm', 'Naozaj zmazať túto faktúru?'))) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, { headers: authHeaders() });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
        <Label htmlFor="invoice-filter">{t('admin.invoices_page.filter', 'Filter')}:</Label>
        <select
          id="invoice-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          data-testid="invoice-filter"
        >
          <option value="">{t('admin.invoices_page.all', 'Všetky')}</option>
          <option value="draft">{t('admin.invoices_page.status_draft', 'Návrhy')}</option>
          <option value="sent">{t('admin.invoices_page.status_sent', 'Odoslané')}</option>
          <option value="paid">{t('admin.invoices_page.status_paid', 'Zaplatené')}</option>
          <option value="cancelled">{t('admin.invoices_page.status_cancelled', 'Zrušené')}</option>
        </select>
        <span className="text-sm text-gray-500 ml-auto">
          {invoices.length} {t('admin.invoices_page.count_word', 'faktúr')}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left">{t('admin.invoices_page.col_number', 'Číslo')}</th>
              <th className="px-4 py-3 text-left">{t('admin.invoices_page.col_buyer', 'Odberateľ')}</th>
              <th className="px-4 py-3 text-right">{t('admin.invoices_page.col_amount', 'Suma')}</th>
              <th className="px-4 py-3 text-left">{t('admin.invoices_page.col_due', 'Splatnosť')}</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">{t('admin.invoices_page.col_actions', 'Akcie')}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">
                  {t('admin.invoices_page.empty', 'Žiadne faktúry. Vytvorte prvú cez AI generátor vyššie.')}
                </td>
              </tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-gray-100" data-testid={`invoice-row-${inv.number}`}>
                <td className="px-4 py-3 font-mono">{inv.number}</td>
                <td className="px-4 py-3">
                  <div>{inv.buyer_name}</div>
                  <div className="text-xs text-gray-500">{inv.buyer_email}</div>
                </td>
                <td className="px-4 py-3 text-right font-semibold">{fmtMoney(inv.total, inv.currency)}</td>
                <td className="px-4 py-3">{fmtDate(inv.due_date, lang)}</td>
                <td className="px-4 py-3">
                  <Badge className={STATUS_COLORS[inv.status] || ''}>{inv.status}</Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end items-center gap-1">
                    <button
                      onClick={() => downloadPdf(inv.id, inv.number)}
                      title="PDF"
                      className="p-2 hover:bg-gray-100 rounded"
                      data-testid={`pdf-${inv.number}`}
                      disabled={busyId === inv.id}
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {inv.buyer_email && inv.status !== 'paid' && (
                      <button
                        onClick={() => sendEmail(inv.id)}
                        title={t('admin.invoices_page.send_email', 'Odoslať e-mailom')}
                        className="p-2 hover:bg-gray-100 rounded text-orange-600"
                        data-testid={`send-${inv.number}`}
                        disabled={busyId === inv.id}
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    )}
                    {inv.status !== 'paid' && (
                      <button
                        onClick={() => markPaid(inv.id)}
                        title={t('admin.invoices_page.mark_paid', 'Označiť ako zaplatené')}
                        className="p-2 hover:bg-gray-100 rounded text-green-600"
                        data-testid={`paid-${inv.number}`}
                        disabled={busyId === inv.id}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => remove(inv.id)}
                      title={t('admin.invoices_page.delete', 'Zmazať')}
                      className="p-2 hover:bg-gray-100 rounded text-red-500"
                      data-testid={`del-${inv.number}`}
                      disabled={busyId === inv.id}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminInvoices = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('list');
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2" data-testid="admin-invoices-title">
          <FileText className="h-6 w-6 text-orange-500" />
          {t('admin.invoices_page.title', 'Faktúry')}
        </h2>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list">
            <FileText className="h-4 w-4 mr-2" /> {t('admin.invoices_page.tab_list', 'Zoznam')}
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Sparkles className="h-4 w-4 mr-2" /> {t('admin.invoices_page.tab_ai', 'AI generátor')}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="h-4 w-4 mr-2" /> {t('admin.invoices_page.tab_settings', 'Reqvizity')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <InvoiceList refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="ai">
          <AiTab onCreated={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInvoices;
